// https://shopify.dev/api/examples/cart

// The CartContext provides very similar functionality to the useCart hook
// from Shopify Hydrogen: https://shopify.dev/api/hydrogen/hooks/cart/usecart

// This component was created before Shopify Hydrogen existsed, and so uses a
// slightly different syntax. It also provides greater encapsulation of the GraphQL
// queries in the Shopify API, and has additional application-specific customisation.

import * as React from 'react';
import PropTypes from 'prop-types';
import { useLocalStorage } from 'utils/frontend/storage-hooks';
import * as Sentry from '@sentry/gatsby';
import { NotificationContext } from 'context/Notification';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import { useQuery as useRQQuery } from 'react-query';
import {
  FREE_GIFT_DISMISSED_ATTRIBUTE_KEY,
  FREE_GIFT_LINE_ATTRIBUTE_KEY,
  FREE_GIFT_PROMOTION_ID_ATTRIBUTE_KEY,
  cartHasQualifyingProduct,
  coerceFreeGiftPromotions,
  dismissedKeyForPromotion,
  findAllFreeGiftLines,
  findFreeGiftLineForPromotion,
  getCartSubtotalExcludingGift,
  getFreeGiftPromoState,
  getGiftPromotionId,
  getPromotionKey,
  getQualifyingSubtotal,
  isFreeGiftDismissedForPromotion,
  isGiftLineTotalZero,
} from 'utils/frontend/free-gift';
import { normalizeShopifyId } from 'utils/frontend/storewide-sale';
import CartActions from './actions';

const {
  createCart,
  retrieveCart,
  addVariant,
  removeLineItem,
  updateLineItemQuantity,
  updateOrderInstructions,
  setBuyerIdentity,
  removeBuyerIdentity,

  // Attribute mutations are renamed to avoid naming conflict with exposed actions
  setAttribute: setCartAttribute,
  removeAttribute: removeCartAttribute
} = CartActions;

const ATTRIBUTE_KEYS = {
  GIFT_NOTE_MESSAGE: "Gift note message",
};

const isBrowser = typeof window !== 'undefined';

const ssrValue = {
  ATTRIBUTE_KEYS,
  data: {
    lineItems: { nodes: [] },
  },
  initialised: true,
  updating: false,
  updatingAttribute: false,
  containsGiftNote: false,
  freeGift: {
    promoState: 'unknown',
    promotions: [],
    cartSubtotalExcludingGift: 0,
  },
  actions: {
    getProductQuantity: () => 0,
    getAttributeValue: () => null,
    dismissFreeGift: async () => false,
  }
};

export const CartContext = React.createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = React.useState(null);
  const [localCartId, setLocalCartId] = useLocalStorage("shopify_cart_id");

  // Reflects when ANY update is being performed on the cart instance
  const [updating, setUpdating] = React.useState(false);

  // Reflects when a cart attribute is being updated
  // (false when inactive, or the key of the attribute being updating when active)
  const [updatingAttribute, setUpdatingAttribute] = React.useState(false);

  const { showNotification } = React.useContext(NotificationContext);

  const freeGiftPromoRequest = useRQQuery(
    'active_free_gift_promotion',
    async () => {
      const { data } = await axios.get('/api/promotions/free-gift');
      return data;
    },
    {
      retry: false,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const freeGiftPromoState = React.useMemo(() => (
    getFreeGiftPromoState({
      isError: freeGiftPromoRequest.isError,
      isSuccess: freeGiftPromoRequest.isSuccess,
      data: freeGiftPromoRequest.data,
    })
  ), [freeGiftPromoRequest.isError, freeGiftPromoRequest.isSuccess, freeGiftPromoRequest.data]);

  const freeGiftPromotions = React.useMemo(() => (
    coerceFreeGiftPromotions(freeGiftPromoRequest.data)
  ), [freeGiftPromoRequest.data]);

  React.useEffect(() => {
    if (process.env.GATSBY_NODE_ENV !== 'development') {
      return;
    }

    if (freeGiftPromoRequest.isError) {
      console.warn('[free-gift] Failed to fetch promotion config:', freeGiftPromoRequest.error);
      return;
    }

    if (!freeGiftPromoRequest.isSuccess) {
      return;
    }

    if (freeGiftPromotions.length === 0) {
      console.warn('[free-gift] No active promotions returned by /api/promotions/free-gift', freeGiftPromoRequest.data?._debug);
    } else {
      console.info('[free-gift] Active promotions:', freeGiftPromotions);
    }
  }, [
    freeGiftPromoRequest.isError,
    freeGiftPromoRequest.isSuccess,
    freeGiftPromoRequest.error,
    freeGiftPromoRequest.data,
    freeGiftPromotions,
  ]);

  const giftEffectInFlight = React.useRef(false);
  // Map of promotionKey -> giftTitle for toasts waiting on $0 confirmation
  const pendingGiftEarnedToasts = React.useRef(new Map());

  // Create or retrieve the cart on initialisation
  React.useEffect(() => {
    const invoke = async () => {
      try {
        let initialCart;

        // Retrieve the existing cart using the shopify_cart_id local storage value if it exists
        if (localCartId) {
          initialCart = await retrieveCart(localCartId);
        }

        // If there is no cart ID stored in local storage, or if no cart was returned from the retrieval, create a new cart instead
        // (a failed retrieval can be caused if the cart has deleted either due to completing checkout, or inactivity)
        // (currently carts expire after 10 days of inactivity, refer to: https://shopify.dev/api/examples/cart#considerations)
        if (!initialCart) {
          initialCart = await createCart();
          
          // Save the cart ID in local storage
          setLocalCartId(initialCart.shopifyId);
        }

        setCart(initialCart);

      } catch (error) {
        showNotification({
          type: 'error',
          message: 'There was an error loading the cart',
        });
        Sentry.captureException(error, {
          tags: {
            action: 'initialise_cart'
          }
        });
      }
    };
    invoke();
  }, []);

  // Track state and handle errors when exposed actions are invoked
  const performAction = React.useCallback(async (invoke, sentryActionTag, notifications = {}) => {
    const toastId = notifications.invoked ? showNotification({
      type: 'loading',
      message: notifications.invoked
    }) : null;
    setUpdating(true);
    const onComplete = (result) => {
      setUpdating(false);
      return result;
    };
    try {
      const newCart = await invoke();
      setCart(newCart);
      if (notifications.success) {
        showNotification({
          type: 'success',
          message: notifications.success, 
          options: {
            id: toastId
          }
        });
      }
      return onComplete(true);
    }
    catch (error) {
      console.log('error', error);
      if (notifications.error) {
        showNotification({
          type: 'error',
          message: notifications.error,
          options: {
            id: toastId
          }
        });
      }
      Sentry.captureException(error, {
        tags: {
          action: sentryActionTag || 'cart_action'
        }
      });
      return onComplete(false);
    }
  }, [setUpdating, setCart, showNotification]);

  const performSilentAction = React.useCallback(async (invoke, sentryActionTag) => (
    performAction(invoke, sentryActionTag, {
      invoked: null,
      success: null,
      error: null,
    })
  ), [performAction]);

  const buildVariantFromLineItem = React.useCallback((lineItem) => ({
    shopifyId: lineItem.variant.shopifyId,
    title: lineItem.variant.title,
    displayName: `${lineItem.variant.product.title} - ${lineItem.variant.title}`.replace(' - Default Title', ''),
    sku: lineItem.variant.sku,
    priceV2: lineItem.variant.priceV2,
    product: {
      title: lineItem.variant.product.title,
    },
  }), []);

  const removeFreeGiftLine = React.useCallback(async (lineItem) => (
    performSilentAction(async () => (
      removeLineItem(cart, {
        lineItem: { shopifyId: lineItem.shopifyId },
        variant: buildVariantFromLineItem(lineItem),
        silent: true,
      })
    ), 'remove_free_gift_line')
  ), [cart, performSilentAction, buildVariantFromLineItem]);

  // Auto-add / auto-remove free gifts based on active Shopify promotions.
  // At most one cart-mutation group per run; the effect re-runs after cart updates.
  React.useEffect(() => {
    if (!cart || updating || giftEffectInFlight.current || freeGiftPromoState === 'unknown') {
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      giftEffectInFlight.current = true;

      try {
        // Confirm $0 pricing before toasting earned gifts
        for (const [promoKey, giftTitle] of pendingGiftEarnedToasts.current.entries()) {
          const promotion = freeGiftPromotions.find((p) => getPromotionKey(p) === promoKey);
          if (!promotion) {
            pendingGiftEarnedToasts.current.delete(promoKey);
            continue;
          }

          const line = findFreeGiftLineForPromotion(cart, promotion);
          if (line && isGiftLineTotalZero(line)) {
            showNotification({
              type: 'success',
              message: `You've earned a free ${giftTitle}!`,
            });
            pendingGiftEarnedToasts.current.delete(promoKey);
          }
        }

        // Clear legacy global dismissal attribute if present
        if (cart.attributes?.some(({ key, value }) => (
          key === FREE_GIFT_DISMISSED_ATTRIBUTE_KEY && value === 'true'
        ))) {
          await performSilentAction(async () => (
            removeCartAttribute(cart, {
              attribute: { key: FREE_GIFT_DISMISSED_ATTRIBUTE_KEY },
            })
          ), 'clear_legacy_free_gift_dismissed');
          return;
        }

        const activePromotionKeys = new Set(
          freeGiftPromotions.map(getPromotionKey).filter(Boolean)
        );

        // Remove gift lines that no longer map to an active promotion
        const staleGiftLine = findAllFreeGiftLines(cart).find((lineItem) => {
          const linePromoId = getGiftPromotionId(lineItem);
          if (linePromoId) {
            return !activePromotionKeys.has(normalizeShopifyId(linePromoId));
          }

          // Legacy gift line without promo-id: keep only if some active promo still gifts that variant
          const variantId = normalizeShopifyId(lineItem.variant?.shopifyId);
          return !freeGiftPromotions.some((promotion) => (
            normalizeShopifyId(promotion.giftVariantId) === variantId
          ));
        });

        if (staleGiftLine) {
          await removeFreeGiftLine(staleGiftLine);
          return;
        }

        for (const promotion of freeGiftPromotions) {
          const promotionKey = getPromotionKey(promotion);
          if (!promotionKey) {
            continue;
          }

          const freeGiftLine = findFreeGiftLineForPromotion(cart, promotion);
          const qualifyingSubtotal = getQualifyingSubtotal(cart, promotion);
          const dismissed = isFreeGiftDismissedForPromotion(cart, promotion);
          const dismissedKey = dismissedKeyForPromotion(promotion);
          const { threshold, giftVariantId, giftTitle } = promotion;

          if (qualifyingSubtotal < threshold) {
            if (freeGiftLine) {
              await removeFreeGiftLine(freeGiftLine);
              return;
            }

            if (dismissed && dismissedKey) {
              await performSilentAction(async () => (
                removeCartAttribute(cart, {
                  attribute: { key: dismissedKey },
                })
              ), 'clear_free_gift_dismissed');
              return;
            }

            continue;
          }

          if (dismissed) {
            if (freeGiftLine) {
              await removeFreeGiftLine(freeGiftLine);
              return;
            }
            continue;
          }

          if (freeGiftLine && freeGiftLine.quantity > 1) {
            await performSilentAction(async () => (
              updateLineItemQuantity(cart, {
                lineItem: {
                  shopifyId: freeGiftLine.shopifyId,
                  quantity: 1,
                },
                variant: {
                  shopifyId: freeGiftLine.variant.shopifyId,
                },
                silent: true,
              })
            ), 'clamp_free_gift_quantity');
            return;
          }

          if (freeGiftLine && !isGiftLineTotalZero(freeGiftLine)) {
            // Shopify declined to combine this gift — suppress this promo for the cart
            await removeFreeGiftLine(freeGiftLine);
            if (dismissedKey) {
              await performSilentAction(async () => (
                setCartAttribute(cart, {
                  attribute: {
                    key: dismissedKey,
                    value: 'true',
                  },
                })
              ), 'suppress_non_combined_free_gift');
            }
            pendingGiftEarnedToasts.current.delete(promotionKey);
            return;
          }

          if (freeGiftLine) {
            continue;
          }

          const added = await performSilentAction(async () => (
            addVariant(cart, {
              variant: {
                shopifyId: giftVariantId,
                title: 'Default Title',
                displayName: giftTitle,
                product: { title: giftTitle },
              },
              quantity: 1,
              attributes: [
                { key: FREE_GIFT_LINE_ATTRIBUTE_KEY, value: 'true' },
                { key: FREE_GIFT_PROMOTION_ID_ATTRIBUTE_KEY, value: promotionKey },
              ],
              silent: true,
            })
          ), 'add_free_gift_line');

          if (added) {
            pendingGiftEarnedToasts.current.set(promotionKey, giftTitle);
          }
          return;
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { action: 'free_gift_effect' },
        });
      } finally {
        if (!cancelled) {
          giftEffectInFlight.current = false;
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      giftEffectInFlight.current = false;
    };
  }, [
    cart,
    updating,
    freeGiftPromoState,
    freeGiftPromotions,
    performSilentAction,
    removeFreeGiftLine,
    showNotification,
  ]);

  // Keep the buyer identity of the cart in sync with the customer identified
  // by the access token cookie
  const [cookies] = useCookies(['customer_access_token']);
  React.useEffect(() => {
    const invoke = async () => {
      if (cart) {
        const { buyerIdentity } = cart;
        const { customer } = buyerIdentity;
        const cartHasBuyerIdentity = !!customer;

        if (cartHasBuyerIdentity) {
          if (cookies.customer_access_token) {
          // Verify that the the cart buyer identity is the customer 
          // represented by the access token cookie (to prevent mismatch
          // between the buyer identity and logged in customer)
          // https://shopify.dev/api/storefront/2022-07/queries/customer

            // TODO

          } else {
          // Cart has a buyer identity but there is no access token
          // Clear buyer identity from cart
            await performAction(async () => (
              removeBuyerIdentity(cart)
            ), 'remove_cart_buyer_identity');
          }
        }
      
        else if (!cartHasBuyerIdentity && cookies.customer_access_token) {
        // Cart does not have a buyer identity but an access token exists
        // Set buyer identity on the cart
          await performAction(async () => (
            setBuyerIdentity(cart, {
              accessToken: cookies.customer_access_token
            })
          ), 'set_cart_buyer_identity');
        }
      }
    };
    invoke();
  }, [cart, cookies.customer_access_token]);

  // Shopify products that only have one default variant do not have a variant
  // name and instead use "Default Title", so this removes this trailing text
  // from the variant display name
  const cleanDisplayName = React.useCallback(displayName => (
    displayName.replace(' - Default Title', "")
  ), []);

  const normalizeCheckoutUrl = React.useCallback((url) => {
    if (url.includes('myshopify.com')) {
      return url.replace(
        process.env.GATSBY_SHOPIFY_URL,
        process.env.GATSBY_SHOPIFY_CHECKOUT_URL
      );
    }

    if ((new RegExp(`http(s?)://${process.env.GATSBY_SITE_URL}`)).test(url)) {
      return url.replace(
        process.env.GATSBY_SITE_URL,
        process.env.GATSBY_SHOPIFY_CHECKOUT_URL
      );
    }

    return url;
  }, []);

  // Navigates the customer to the checkout once that have confirmed their cart
  const goToCheckout = React.useCallback(async () => {
    if (!cart) {
      throw Error('Cart not yet initialised');
    }

    let currentCart = cart;

    // Final gate: never send a non-$0 gift line to checkout
    const nonZeroGiftLines = findAllFreeGiftLines(currentCart).filter(
      (lineItem) => !isGiftLineTotalZero(lineItem)
    );

    if (nonZeroGiftLines.length > 0) {
      try {
        setUpdating(true);
        for (const freeGiftLine of nonZeroGiftLines) {
          currentCart = await removeLineItem(currentCart, {
            lineItem: { shopifyId: freeGiftLine.shopifyId },
            variant: buildVariantFromLineItem(freeGiftLine),
            silent: true,
          });
        }
        setCart(currentCart);
      } catch (error) {
        showNotification({
          type: 'error',
          message: 'There was an issue updating your cart before checkout. Please try again.',
        });
        Sentry.captureException(error, {
          tags: { action: 'checkout_remove_invalid_free_gift' },
        });
        return;
      } finally {
        setUpdating(false);
      }
    }

    let checkoutUrl = normalizeCheckoutUrl(currentCart.checkoutUrl);

    // Bind the customer to the checkout URL if logged in (i.e. an access token cookie exists)
    if (cookies.customer_access_token) {
      try {
        let clientIPAddress;
        try {
          const { data } = await axios.get('https://geolocation-db.com/json/');
          clientIPAddress = data.IPv4;
        } catch (error) {
          throw Error('Error fetching the client IP address');
        }

        const { data: { checkoutUrl: boundCheckoutUrl } } = await axios.post('/api/customer/bind-checkout-url', {
          checkoutUrl,
          clientIPAddress
        });

        checkoutUrl = boundCheckoutUrl;
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            action: 'bind_checkout_url_to_customer'
          }
        });
      }
    }

    window.location.href = checkoutUrl;
  }, [cart, cookies.customer_access_token, buildVariantFromLineItem, showNotification, normalizeCheckoutUrl, setUpdating, setCart]);

  // Wraps the setCartAttribute function to enable state tracking
  const setAttribute = React.useCallback(async (_, { key, value }) => {
    setUpdatingAttribute(key);
    try {
      const newCart = await setCartAttribute(cart, {
        attribute: { key, value }
      });
      setUpdatingAttribute(false);
      return newCart;
    } catch (error) {
      setUpdatingAttribute(false);
      throw error;
    }
  }, [cart, setUpdatingAttribute]);


  // Wraps the removeCartAttribute function to enable state tracking
  const removeAttribute = React.useCallback(async (_, key) => {
    setUpdatingAttribute(key);
    try {
      const newCart = await removeCartAttribute(cart, {
        attribute: { key }
      });
      setUpdatingAttribute(false);
      return newCart;
    } catch (error) {
      setUpdatingAttribute(false);
      throw error;
    }
  }, [cart, setUpdatingAttribute]);


  const getAttributeValue = React.useCallback((key) => (
    cart ? (
      cart.attributes.find((attribute) => attribute.key === key)?.value ?? null
    ) : (
      null
    )
  ), [cart]);


  // Wrap each of the exposed actions with the above performAction function
  const actions = React.useMemo(() => (
    [
      {
        name: 'addVariant',
        func: addVariant,
        sentryActionTag: 'add_variant_to_cart',
        notifications: {
          invoked: ({ variant, quantity }) => `Adding ${quantity} x ${cleanDisplayName(variant.displayName)} to your cart...`,
          success: ({ variant, quantity }) => `Added ${quantity} x ${cleanDisplayName(variant.displayName)} to your cart!`,
          error: ({ variant, quantity }) => `Error adding ${quantity} x ${cleanDisplayName(variant.displayName)} to your cart`,
        },
      },
      {
        name: 'removeLineItem',
        func: removeLineItem,
        sentryActionTag: 'remove_cart_line_item',
        notifications: {
          invoked: ({ variant }) => `Removing ${cleanDisplayName(variant.displayName)} from your cart...`,
          success: ({ variant }) => `Removed ${cleanDisplayName(variant.displayName)} from your cart!`,
          error: ({ variant }) => `Error removing ${cleanDisplayName(variant.displayName)} from your cart`,
        },
      },
      {
        name: 'updateLineItemQuantity',
        func: updateLineItemQuantity,
        sentryActionTag: 'update_cart_line_item_quantity',
        notifications: {
          invoked: () => `Updating your cart...`,
          success: ({ variant, lineItem }) => `There ${lineItem.quantity === 1 ? 'is' : 'are'} now ${lineItem.quantity} x ${cleanDisplayName(variant.displayName)} in your cart!`,
          error: () => `Error updating cart`,
        },
      },
      {
        name: 'updateOrderInstructions',
        func: updateOrderInstructions,
        sentryActionTag: 'update_cart_order_instructions',
        notifications: {
          invoked: null,
          success: null,
          error: () => 'There was an error setting your order instructions',
        },
      },
      {
        name: 'setAttribute',
        func: setAttribute,
        sentryActionTag: 'set_cart_attribute',
        notifications: {
          invoked: null,
          success: null,
          error: () => 'There was an error updating your cart',
        },
      },
      {
        name: 'removeAttribute',
        func: removeAttribute,
        sentryActionTag: 'remove_cart_attribute',
        notifications: {
          invoked: null,
          success: null,
          error: () => 'There was an error updating your cart',
        },
      },
    ]
      .reduce((current, { name, func, sentryActionTag, notifications }) => ({
        ...current,
        [name]: (args) => (
          performAction(
            async () => func(cart, args),
            sentryActionTag,
            {
              // Evaluate the notification messages based on the a provided arguments
              // (only if a notification message evaluation function exists)
              invoked: notifications.invoked?.call(null, args),
              success: notifications.success?.call(null, args),
              error: notifications.error?.call(null, args),
            },
          )
        )
      }), {})
  ), [cart, performAction, showNotification, setUpdating]);

  // Check that there is an environment variable for the gift note product
  const giftNoteProductExists = React.useMemo(() => (
    !!process.env.GATSBY_SHOPIFY_GIFT_NOTE_PRODUCT_ID
  ), []);

  // Check if the personalised gift note is within the list of line items
  const containsGiftNote = React.useMemo(() => (!cart || !giftNoteProductExists) ? false : (
    cart.lineItems.nodes.some((lineItem) => (
      lineItem.variant.product.shopifyId === process.env.GATSBY_SHOPIFY_GIFT_NOTE_PRODUCT_ID
    ))
  ), [cart, giftNoteProductExists]);

  // If only digitial gift cards are in the cart then this will be false
  // (used to prevent adding physical gift note to digital gift cards)
  const hasPhysicalItems = React.useMemo(() => (
    !!cart?.lineItems.nodes.some(({ variant }) => variant.requiresShipping)
  ), [cart]);

  // Add the quantities for each of the line items that is a variant of the specified product
  const getProductQuantity = React.useCallback((shopifyProductId) => (
    cart ? (
      cart.lineItems.nodes.reduce((currentTotal, lineItem) => (
        lineItem.variant.product.shopifyId === shopifyProductId ? (
          currentTotal + lineItem.quantity
        ) : currentTotal
      ), 0)
    ) : 0
  ), [cart]);
  

  const dismissFreeGift = React.useCallback(async (promotionKey) => {
    if (!promotionKey || !cart) {
      return false;
    }

    return performSilentAction(async () => (
      setCartAttribute(cart, {
        attribute: {
          key: `__free_gift_dismissed_${promotionKey}`,
          value: 'true',
        },
      })
    ), 'dismiss_free_gift');
  }, [cart, performSilentAction]);

  const cartSubtotalExcludingGift = React.useMemo(() => (
    cart ? getCartSubtotalExcludingGift(cart) : 0
  ), [cart]);

  const freeGiftPromotionEntries = React.useMemo(() => (
    freeGiftPromotions.map((promotion) => ({
      promotion,
      qualifyingSubtotal: cart ? getQualifyingSubtotal(cart, promotion) : 0,
      hasQualifyingProduct: cart ? cartHasQualifyingProduct(cart, promotion) : false,
      line: cart ? findFreeGiftLineForPromotion(cart, promotion) : null,
      isDismissed: cart ? isFreeGiftDismissedForPromotion(cart, promotion) : false,
    }))
  ), [cart, freeGiftPromotions]);

  const value = React.useMemo(() => ({
    ATTRIBUTE_KEYS,
    data: cart,
    initialised: !!cart,
    updating,
    updatingAttribute,
    containsGiftNote,
    hasPhysicalItems,
    freeGift: {
      promoState: freeGiftPromoState,
      promotions: freeGiftPromotionEntries,
      cartSubtotalExcludingGift,
    },
    actions: {
      ...actions,
      getAttributeValue,
      goToCheckout,
      getProductQuantity,
      dismissFreeGift,
    }
  }), [
    cart,
    updating,
    updatingAttribute,
    containsGiftNote,
    hasPhysicalItems,
    freeGiftPromoState,
    freeGiftPromotionEntries,
    cartSubtotalExcludingGift,
    actions,
    goToCheckout,
    getProductQuantity,
    dismissFreeGift,
  ]);

  return (
    <CartContext.Provider value={isBrowser ? value : ssrValue}>
      {children}
    </CartContext.Provider>
  );
};


CartProvider.propTypes = {
  children: PropTypes.node.isRequired
};